import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useMemo, useSyncExternalStore } from 'react';
import { getRuntimeEnvValue } from './runtimeEnv';
import { resolveServiceRequestUrl } from './utils/serviceRequestUrl';
const FLAG_REFRESH_INTERVAL_MS = 30000;
const FeatureFlagsContext = createContext({});
const FeatureFlagsActiveContext = createContext(null);
export function getConfiguredFeatureFlagEndpoint(endpointSource = 'telematics', hostApp) {
    if (hostApp === 'FPG') {
        const apiUrl = getRuntimeEnvValue('API_URL');
        return resolveServiceRequestUrl({
            targetUrl: apiUrl
                ? `${apiUrl.endsWith('/') ? apiUrl : `${apiUrl}/`}api/v1/feature`
                : undefined,
            hostApp,
            legacyEndpoint: '',
        });
    }
    if (getRuntimeEnvValue('APP_ENVIRONMENT') !== 'local') {
        const proxyKey = endpointSource === 'telematics'
            ? 'TELEMATICS_FEATURE_FLAGS_PROXY_URL'
            : 'UNIFIED_UI_FEATURE_FLAGS_PROXY_URL';
        const proxyUrl = (typeof window !== 'undefined' ? window.serviceManagementEnv?.[proxyKey] : undefined)
            ?? getRuntimeEnvValue(proxyKey);
        if (proxyUrl) {
            const apiUrl = getRuntimeEnvValue('API_URL');
            if (!apiUrl) {
                console.warn('API_URL is required for proxied feature-flag requests. Polling is disabled.');
                return '';
            }
            const targetUrl = `${apiUrl.endsWith('/') ? apiUrl : `${apiUrl}/`}api/v1/feature`;
            return proxyUrl + (/[?&]resourceUrl=$/.test(proxyUrl) ? targetUrl : encodeURIComponent(targetUrl));
        }
    }
    return endpointSource === 'telematics'
        ? getRuntimeEnvValue('TELEMATICS_FEATURE_FLAGS_ENDPOINT') ?? ''
        : getRuntimeEnvValue('UNIFIED_UI_FEATURE_FLAGS_ENDPOINT') ?? '';
}
function createFeatureFlagStore(endpointSource, hostApp) {
    let initialized = false;
    let endpoint = '';
    let flags = {};
    let subscribers = [];
    let pollingActive = false;
    let pollingInterval = null;
    let pollingGeneration = 0;
    const notifySubscribers = () => {
        subscribers.forEach((callback) => callback());
    };
    const stopPolling = () => {
        if (pollingInterval !== null && typeof window !== 'undefined') {
            window.clearInterval(pollingInterval);
        }
        pollingInterval = null;
        pollingActive = false;
        pollingGeneration += 1;
    };
    const startPolling = () => {
        if (pollingActive || !endpoint) {
            return;
        }
        pollingActive = true;
        const fetchFlags = async () => {
            const generation = pollingGeneration;
            try {
                const response = await fetch(endpoint, {
                    method: 'GET',
                    credentials: 'include',
                    headers: { Accept: 'application/json' },
                });
                if (!response.ok)
                    return;
                const payload = (await response.json());
                const resolvedFlags = typeof payload === 'object' && payload !== null && 'data' in payload
                    ? payload.data
                    : payload;
                if (generation === pollingGeneration &&
                    resolvedFlags &&
                    typeof resolvedFlags === 'object') {
                    flags = resolvedFlags;
                    notifySubscribers();
                }
            }
            catch {
                // Polling should not block the app when endpoint is unavailable.
            }
        };
        void fetchFlags();
        if (typeof window !== 'undefined') {
            pollingInterval = window.setInterval(fetchFlags, FLAG_REFRESH_INTERVAL_MS);
        }
    };
    return {
        initialize() {
            if (initialized) {
                return;
            }
            endpoint = getConfiguredFeatureFlagEndpoint(endpointSource, hostApp);
            initialized = true;
            if (subscribers.length > 0) {
                startPolling();
            }
        },
        subscribe(callback) {
            subscribers.push(callback);
            if (initialized) {
                startPolling();
            }
            return () => {
                subscribers = subscribers.filter((subscriber) => subscriber !== callback);
                if (subscribers.length === 0) {
                    stopPolling();
                }
            };
        },
        getSnapshot() {
            return flags;
        },
    };
}
const featureFlagStores = new Map();
function getFeatureFlagStoreKey(endpointSource, hostApp) {
    return `${endpointSource}:${hostApp === 'FPG' ? 'FPG' : 'legacy'}`;
}
function getFeatureFlagStore(endpointSource, hostApp) {
    const key = getFeatureFlagStoreKey(endpointSource, hostApp);
    const existingStore = featureFlagStores.get(key);
    if (existingStore) {
        return existingStore;
    }
    const store = createFeatureFlagStore(endpointSource, hostApp);
    featureFlagStores.set(key, store);
    return store;
}
export function initializeFeatureFlags(endpointSource = 'telematics', hostApp) {
    getFeatureFlagStore(endpointSource, hostApp).initialize();
}
export function FeatureFlagsRoot({ children, endpointSource = 'telematics', hostApp, }) {
    const activeSource = useContext(FeatureFlagsActiveContext);
    if (activeSource === getFeatureFlagStoreKey(endpointSource, hostApp)) {
        return _jsx(_Fragment, { children: children });
    }
    return (_jsx(FeatureFlagsRootInner, { endpointSource: endpointSource, hostApp: hostApp, children: children ?? null }));
}
function FeatureFlagsRootInner({ children, endpointSource, hostApp, }) {
    const store = getFeatureFlagStore(endpointSource, hostApp);
    store.initialize();
    const flags = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
    const value = useMemo(() => flags, [flags]);
    return (_jsx(FeatureFlagsActiveContext.Provider, { value: getFeatureFlagStoreKey(endpointSource, hostApp), children: _jsx(FeatureFlagsContext.Provider, { value: value, children: children }) }));
}
export function useFeatureFlag(flagName) {
    const featureFlags = useContext(FeatureFlagsContext);
    return featureFlags[flagName] ?? false;
}
export function useStandaloneFeatureFlag(flagName, endpointSource = 'telematics', enabled = true, hostApp) {
    const store = getFeatureFlagStore(endpointSource, hostApp);
    if (enabled) {
        store.initialize();
    }
    const flags = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
    return flags[flagName] ?? false;
}
